<template>
    <div class="min-h-screen overflow-x-hidden pb-10">
        <!-- Breadcrumb -->
        <a class="mt-5 b_title">
            <div class="flex items-center flex-wrap">
                <router-link :to="{ name: 'FloodReliefFuelRequests' }" class="cursor-pointer text-emerald-700 hover:text-emerald-900 text-sm sm:text-base">
                    <span>คำขอเบิกค่าเชื้อเพลิง</span>
                </router-link>
                &nbsp;
                <div class="flex items-center text-gray-600 text-sm sm:text-base">
                    <span> / จัดการคำขอ</span>
                </div>
            </div>
        </a>

        <!-- Loading state -->
        <div v-if="loading" class="mt-6 bg-white rounded-lg shadow p-8 space-y-4 animate-pulse">
            <div class="h-4 bg-gray-100 rounded w-1/3"></div>
            <div class="h-4 bg-gray-100 rounded w-2/3"></div>
            <div class="h-64 bg-gray-100 rounded"></div>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="mt-6 bg-white rounded-lg shadow p-6 border border-red-200">
            <p class="text-red-600 font-semibold mb-2">{{ error }}</p>
            <button type="button" class="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded hover:bg-red-600" @click="fetchRequest">
                ลองอีกครั้ง
            </button>
        </div>

        <!-- Loaded content -->
        <div v-else class="max-w-4xl mx-auto px-2 sm:px-0">
            <!-- Summary cards -->
            <div class="grid gap-3 sm:gap-4 mt-6 grid-cols-1 sm:grid-cols-3">
                <div class="p-4 sm:p-5 bg-white rounded-lg shadow border" :class="getStatusBorderClass(request.status)">
                    <p class="text-xs sm:text-sm text-gray-500">สถานะปัจจุบัน</p>
                    <div class="flex items-center justify-between mt-2 sm:mt-3 flex-wrap gap-2">
                        <span class="px-2 sm:px-3 py-1 text-xs font-semibold rounded-full" :class="getStatusChipClass(request.status)">
                            {{ getStatusLabel(request.status) }}
                        </span>
                        <span class="text-xs text-gray-500">{{ formatDateTime(request.updated_at) }}</span>
                    </div>
                </div>
                <div class="p-4 sm:p-5 bg-white rounded-lg shadow border border-gray-100">
                    <p class="text-xs sm:text-sm text-gray-500">จำนวนเงินที่ขอ</p>
                    <p class="mt-1 sm:mt-2 text-xl sm:text-2xl font-semibold text-gray-900">฿{{ formatCurrency(request.total_amount) }}</p>
                    <p class="text-xs text-gray-400">ลงพื้นที่ {{ formatDate(request.field_date) }}</p>
                </div>
                <div class="p-4 sm:p-5 bg-white rounded-lg shadow border border-gray-100">
                    <p class="text-xs sm:text-sm text-gray-500">เลขที่คำขอ</p>
                    <p class="mt-1 sm:mt-2 text-lg sm:text-xl font-semibold text-gray-900 break-all">{{ request.request_code }}</p>
                    <p class="text-xs text-gray-400">สร้างเมื่อ {{ formatDateTime(request.created_at) }}</p>
                </div>
            </div>

            <!-- Detail sections - Single column layout like Form.vue -->
            <div class="space-y-4 sm:space-y-6 mt-4 sm:mt-6">
                
                <!-- Section 0: User/Member Information (Top) -->
                <section class="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-emerald-600">
                    <div class="flex items-center mb-4 sm:mb-6">
                        <div class="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                            <i class="fas fa-user"></i>
                        </div>
                        <h2 class="ml-3 sm:ml-4 text-base sm:text-xl font-semibold text-gray-800">ข้อมูลบัญชีผู้ใช้และสมาชิก</h2>
                    </div>
                    <div class="space-y-4">
                        <div class="p-4 bg-gray-50 rounded-lg">
                            <p class="text-sm text-gray-500">วิธีการสมัครสมาชิก</p>
                            <div class="mt-1 flex items-center gap-2">
                                <span class="px-3 py-1 text-sm font-medium rounded-full" :class="getProviderClass(request.member)">
                                    <i :class="getProviderIcon(request.member)" class="mr-1"></i>
                                    {{ getProviderLabel(request.member) }}
                                </span>
                            </div>
                        </div>
                        <div class="grid gap-4 sm:grid-cols-2">
                            <div>
                                <p class="text-sm text-gray-500">ชื่อ-นามสกุลสมาชิก</p>
                                <p class="mt-1 font-medium text-gray-900 break-words">{{ getMemberFullName(request.member) }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">อีเมลสมาชิก</p>
                                <p class="mt-1 font-medium text-gray-900 break-all">{{ request.member ? (request.member.mem_email || '-') : '-' }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">เบอร์โทรศัพท์สมาชิก</p>
                                <p class="mt-1 font-medium text-gray-900">{{ request.member ? (request.member.mem_tel || '-') : '-' }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">เพศ</p>
                                <p class="mt-1 font-medium text-gray-900">{{ getGenderLabel(request.member) }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">Line ID</p>
                                <p class="mt-1 font-medium text-gray-900">{{ request.member ? (request.member.mem_line_id || '-') : '-' }}</p>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Section 1: Organization Information -->
                <section class="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-teal-600">
                    <div class="flex items-center mb-4 sm:mb-6">
                        <div class="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                            1
                        </div>
                        <h2 class="ml-3 sm:ml-4 text-base sm:text-xl font-semibold text-gray-800">ข้อมูลกลุ่ม/องค์กร</h2>
                    </div>
                    <div class="space-y-4">
                        <div>
                            <p class="text-sm text-gray-500">ชื่อกลุ่ม/ชมรม/มูลนิธิ/องค์กร</p>
                            <p class="mt-1 font-medium text-gray-900 break-words">{{ request.organization_name || '-' }}</p>
                        </div>
                        <div>
                            <p class="text-sm text-gray-500">ที่อยู่ขององค์กร</p>
                            <p class="mt-1 font-medium text-gray-900 whitespace-pre-line break-words">{{ request.organization_address || '-' }}</p>
                        </div>
                        <div>
                            <p class="text-sm text-gray-500">จังหวัดขององค์กร</p>
                            <p class="mt-1 font-medium text-gray-900">{{ getProvinceDisplay(request) }}</p>
                        </div>
                        <div v-if="request.social_media_link">
                            <p class="text-sm text-gray-500">ลิงก์โซเชียลมีเดียขององค์กร</p>
                            <a :href="request.social_media_link" target="_blank" class="mt-1 text-emerald-600 hover:underline inline-flex items-center gap-1 break-all text-sm">
                                <i class="fas fa-link flex-shrink-0"></i>
                                <span class="break-all">{{ request.social_media_link }}</span>
                            </a>
                        </div>
                        <div v-if="request.additional_details">
                            <p class="text-sm text-gray-500">รายละเอียดเพิ่มเติมเกี่ยวกับองค์กร</p>
                            <p class="mt-1 text-gray-800 whitespace-pre-line break-words">{{ request.additional_details }}</p>
                        </div>
                    </div>
                </section>

                <!-- Section 2: Coordinator Information -->
                <section class="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-cyan-600">
                    <div class="flex items-center mb-4 sm:mb-6">
                        <div class="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-cyan-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                            2
                        </div>
                        <h2 class="ml-3 sm:ml-4 text-base sm:text-xl font-semibold text-gray-800">ข้อมูลผู้ประสานงาน</h2>
                    </div>
                    <div class="space-y-4">
                        <div class="grid gap-4 sm:grid-cols-2">
                            <div>
                                <p class="text-sm text-gray-500">ชื่อ-สกุลผู้ประสานงาน</p>
                                <p class="mt-1 font-medium text-gray-900 break-words">{{ request.coordinator_name || '-' }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">เบอร์ติดต่อผู้ประสานงาน</p>
                                <p class="mt-1 font-medium text-gray-900">
                                    <i class="fas fa-phone text-gray-400 mr-1"></i>{{ request.coordinator_phone || '-' }}
                                </p>
                            </div>
                            <div v-if="request.coordinator_email">
                                <p class="text-sm text-gray-500">อีเมลผู้ประสานงาน</p>
                                <p class="mt-1 font-medium text-gray-900 break-all">
                                    <i class="fas fa-envelope text-gray-400 mr-1"></i>{{ request.coordinator_email }}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Section 3: Field Work & Fuel Information -->
                <section class="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-blue-600">
                    <div class="flex items-center mb-4 sm:mb-6">
                        <div class="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                            3
                        </div>
                        <h2 class="ml-3 sm:ml-4 text-base sm:text-xl font-semibold text-gray-800">ข้อมูลการลงพื้นที่และเชื้อเพลิง</h2>
                    </div>
                    <div class="space-y-4">
                        <div class="grid gap-4 sm:grid-cols-2">
                            <div>
                                <p class="text-sm text-gray-500">วันที่ลงพื้นที่</p>
                                <p class="mt-1 font-medium text-gray-900">{{ formatDate(request.field_date) }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">ปีน้ำท่วม</p>
                                <p class="mt-1 font-medium text-gray-900">{{ request.flood_year || '-' }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">น้ำมันเชื้อเพลิงใช้สำหรับ</p>
                                <p class="mt-1 font-medium text-gray-900">{{ request.fuel_for === 'อื่นๆ' ? (request.other_fuel_for || '-') : (request.fuel_for || '-') }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">จำนวนพาหนะ (คัน)</p>
                                <p class="mt-1 font-medium text-gray-900">{{ request.vehicle_count || '-' }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">จำนวนเงินทั้งสิ้น (บาท)</p>
                                <p class="mt-1 text-xl font-semibold text-blue-600">฿{{ formatCurrency(request.total_amount) }}</p>
                            </div>
                        </div>
                        <div>
                            <p class="text-sm text-gray-500">ชื่อชุมชนที่ลงพื้นที่</p>
                            <p class="mt-1 font-medium text-gray-900 whitespace-pre-line break-words">{{ request.community_names || '-' }}</p>
                        </div>
                        <div>
                            <p class="text-sm text-gray-500">จังหวัดที่ลงพื้นที่</p>
                            <div class="flex flex-wrap gap-2 mt-2">
                                <span v-for="province in fieldProvinces" :key="province" class="px-3 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                                    {{ province }}
                                </span>
                                <span v-if="fieldProvinces.length === 0" class="text-sm text-gray-500">ไม่พบข้อมูล</span>
                            </div>
                        </div>
                        <div v-if="request.additional_notes">
                            <p class="text-sm text-gray-500">อธิบายเพิ่มเติม สถานการณ์ / รถที่ไป และอื่นๆ</p>
                            <p class="mt-1 text-gray-800 whitespace-pre-line break-words">{{ request.additional_notes }}</p>
                        </div>
                        <div v-if="request.fieldwork_social_media_link">
                            <p class="text-sm text-gray-500">ลิงก์โซเชียลมีเดียการลงพื้นที่</p>
                            <a :href="request.fieldwork_social_media_link" target="_blank" class="mt-1 text-blue-600 hover:underline inline-flex items-center gap-1 break-all text-sm">
                                <i class="fas fa-link flex-shrink-0"></i>
                                <span class="break-all">{{ request.fieldwork_social_media_link }}</span>
                            </a>
                        </div>
                    </div>
                </section>

                <!-- Section 4: Banking & Documents -->
                <section class="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-indigo-600">
                    <div class="flex items-center mb-4 sm:mb-6">
                        <div class="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                            4
                        </div>
                        <h2 class="ml-3 sm:ml-4 text-base sm:text-xl font-semibold text-gray-800">ข้อมูลการโอนเงินและเอกสาร</h2>
                    </div>
                    <div class="space-y-4">
                        <div class="p-4 bg-indigo-50 rounded-lg">
                            <p class="text-sm text-indigo-600 font-medium mb-3">ข้อมูลบัญชีธนาคาร</p>
                            <div class="grid gap-3 sm:grid-cols-2">
                                <div>
                                    <p class="text-sm text-gray-500">ชื่อธนาคาร</p>
                                    <p class="mt-1 font-medium text-gray-900">{{ resolveBankName(request) }}</p>
                                </div>
                                <div>
                                    <p class="text-sm text-gray-500">ชื่อบัญชี</p>
                                    <p class="mt-1 font-medium text-gray-900 break-words">{{ request.account_name || '-' }}</p>
                                </div>
                                <div>
                                    <p class="text-sm text-gray-500">เลขที่บัญชี</p>
                                    <p class="mt-1 font-medium text-gray-900 font-mono break-all">{{ request.account_number || '-' }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Section 5: Others -->
                <section class="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-purple-600">
                    <div class="flex items-center mb-4 sm:mb-6">
                        <div class="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                            5
                        </div>
                        <h2 class="ml-3 sm:ml-4 text-base sm:text-xl font-semibold text-gray-800">อื่นๆ</h2>
                    </div>
                    <div class="space-y-4">
                        <div>
                            <p class="text-sm text-gray-500">หมายเหตุ/ข้อเสนอแนะจากผู้ยื่น</p>
                            <p class="mt-1 text-gray-800 whitespace-pre-line break-words">{{ request.remarks || '-' }}</p>
                        </div>
                    </div>
                </section>

                <!-- Section: Approval Info (Admin Section) -->
                <section class="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-amber-500">
                    <div class="flex items-center mb-4 sm:mb-6">
                        <div class="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                            <i class="fas fa-check-double"></i>
                        </div>
                        <h2 class="ml-3 sm:ml-4 text-base sm:text-xl font-semibold text-gray-800">ข้อมูลการพิจารณา</h2>
                    </div>
                    <div class="space-y-4">
                        <div class="grid gap-4 sm:grid-cols-2">
                            <div>
                                <p class="text-sm text-gray-500">สถานะการอนุมัติ</p>
                                <span class="inline-block mt-1 px-3 py-1 text-sm font-medium rounded-full" :class="getStatusChipClass(request.status)">
                                    {{ request.status_display || getStatusLabel(request.status) }}
                                </span>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">ผู้อนุมัติ</p>
                                <p class="mt-1 font-medium text-gray-900 break-words">{{ getApproverName(request) }}</p>
                                <p class="text-xs text-gray-500" v-if="request.approved_at">{{ formatDateTime(request.approved_at) }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">จำนวนเงินที่ขอ</p>
                                <p class="mt-1 text-xl font-semibold text-gray-900">฿{{ formatCurrency(request.total_amount) }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-gray-500">จำนวนเงินที่อนุมัติ</p>
                                <p class="mt-1 text-xl font-semibold" :class="request.approved_amount ? 'text-emerald-600' : 'text-gray-400'">
                                    {{ request.approved_amount ? '฿' + formatCurrency(request.approved_amount) : 'ยังไม่ได้ระบุ' }}
                                </p>
                            </div>
                        </div>
                        <div>
                            <p class="text-sm text-gray-500">หมายเหตุจากเจ้าหน้าที่ (ล่าสุด)</p>
                            <p class="mt-1 text-gray-800 whitespace-pre-line break-words">{{ request.admin_notes || '-' }}</p>
                        </div>
                    </div>
                </section>

                <!-- Attachments -->
                <section class="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-gray-400">
                    <div class="flex flex-wrap items-center gap-2 mb-4 sm:mb-6">
                        <div class="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gray-400 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                            <i class="fas fa-paperclip"></i>
                        </div>
                        <h2 class="ml-1 sm:ml-4 text-base sm:text-xl font-semibold text-gray-800">ไฟล์แนบ</h2>
                        <p class="w-full sm:w-auto sm:ml-auto text-xs sm:text-sm text-gray-500">คลิกที่รูปเพื่อดูแบบเต็มหน้าจอ</p>
                    </div>
                    <div v-for="group in attachmentGroups" :key="group.key" class="mt-6 first:mt-0">
                        <div class="flex items-center justify-between">
                            <h4 class="text-sm font-semibold text-gray-800">{{ group.title }}</h4>
                            <span class="text-xs text-gray-500">{{ getFiles(group.key).length }} ไฟล์</span>
                        </div>
                        <div v-if="getFiles(group.key).length" class="grid grid-cols-2 gap-2 sm:gap-4 mt-3 sm:grid-cols-3 lg:grid-cols-4">
                            <button
                                v-for="file in getFiles(group.key)"
                                :key="file.id || file.filename"
                                type="button"
                                class="relative overflow-hidden rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                @click="openLightbox(file, group.title, group.key)"
                            >
                                <img :src="getFileUrl(file, 'thumbnail')" :alt="file.filename" class="object-cover w-full h-28 sm:h-40" />
                                <div class="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-2 py-1 truncate">
                                    {{ file.filename || 'ไฟล์แนบ' }}
                                </div>
                            </button>
                        </div>
                        <p v-else class="mt-2 text-sm text-gray-500">ไม่มีไฟล์แนบ</p>
                    </div>
                </section>

                <!-- Update form -->
                <section class="bg-white rounded-lg shadow p-4 sm:p-6 border-t-4 border-emerald-500">
                    <div class="flex flex-wrap items-center gap-2 mb-4 sm:mb-6">
                        <div class="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                            <i class="fas fa-edit"></i>
                        </div>
                        <h2 class="ml-1 sm:ml-4 text-base sm:text-xl font-semibold text-gray-800">อัปเดตสถานะคำขอ</h2>
                        <span class="w-full sm:w-auto sm:ml-auto text-xs text-gray-500">Request ID: {{ request.id }}</span>
                    </div>
                    <form class="space-y-6" @submit.prevent="handleSubmit">
                        <div class="grid gap-4 sm:grid-cols-2">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">สถานะ <span class="text-red-500">*</span></label>
                                <select
                                    v-model.number="form.status"
                                    class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    :class="{ 'border-red-500': validationErrors.status }"
                                >
                                    <option disabled value="">-- เลือกสถานะ --</option>
                                    <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </select>
                                <p v-if="validationErrors.status" class="mt-1 text-xs text-red-600">{{ validationErrors.status }}</p>
                            </div>
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-1">ยอดเงินที่อนุมัติ (บาท)</label>
                                <input
                                    type="number"
                                    v-model.number="form.approved_amount"
                                    step="0.01"
                                    min="0"
                                    max="100000"
                                    class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    :class="{ 'border-red-500': validationErrors.approved_amount }"
                                    placeholder="ยอดเงินที่อนุมัติ"
                                />
                                <p class="mt-1 text-xs text-gray-500">ยอดเงินที่ขอ: ฿{{ formatCurrency(request.total_amount) }}</p>
                                <p v-if="validationErrors.approved_amount" class="mt-1 text-xs text-red-600">{{ validationErrors.approved_amount }}</p>
                            </div>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">หมายเหตุจากเจ้าหน้าที่</label>
                            <textarea
                                v-model.trim="form.admin_notes"
                                rows="4"
                                class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                :class="{ 'border-red-500': validationErrors.admin_notes }"
                                placeholder="สรุปสั้นๆ เกี่ยวกับเหตุผลและสิ่งที่ต้องดำเนินการต่อ"
                            ></textarea>
                            <p v-if="Number(form.status) === 3" class="mt-1 text-xs text-amber-600">จำเป็นต้องระบุเหตุผลเมื่อไม่อนุมัติคำขอ</p>
                            <p v-if="Number(form.status) === 5" class="mt-1 text-xs text-amber-600">จำเป็นต้องระบุรายละเอียดหลักฐานที่ต้องการเพิ่มเติม</p>
                            <p v-if="validationErrors.admin_notes" class="mt-1 text-xs text-red-600">{{ validationErrors.admin_notes }}</p>
                        </div>
                        
                        <!-- CUCI Paid Slip Upload - แสดงเฉพาะเมื่อสถานะ = 4 (โอนแล้ว) -->
                        <div v-if="Number(form.status) === 4" class="border border-emerald-200 rounded-lg p-4 bg-emerald-50">
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                <i class="fas fa-receipt text-emerald-600 mr-1"></i>
                                แนบสลิปการโอนเงิน (ไม่บังคับ, สูงสุด 3 รูป)
                            </label>
                            
                            <!-- Existing uploaded slips from server -->
                            <div v-if="getFiles('cuci_paid_slips').length" class="mb-3">
                                <p class="text-xs text-gray-600 mb-2">สลิปที่อัปโหลดแล้ว ({{ getFiles('cuci_paid_slips').length }} รูป)</p>
                                <div class="grid grid-cols-3 gap-2">
                                    <div v-for="file in getFiles('cuci_paid_slips')" :key="file.id" class="relative group">
                                        <img 
                                            :src="getFileUrl(file, 'thumbnail')" 
                                            :alt="file.filename" 
                                            class="w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer"
                                            @click="openLightbox(file, 'สลิปการโอนเงินจาก CUCI', 'cuci_paid_slips')"
                                        />
                                        <button 
                                            type="button"
                                            class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                                            @click.stop="deleteExistingCuciPaidSlip(file.id)"
                                            title="ลบสลิปนี้"
                                        >
                                            <i class="fas fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- New file previews to be uploaded -->
                            <div v-if="cuciPaidSlipPreviews.length" class="mb-3">
                                <p class="text-xs text-gray-600 mb-2">รูปใหม่ที่จะอัปโหลด ({{ cuciPaidSlipPreviews.length }} รูป)</p>
                                <div class="grid grid-cols-3 gap-2">
                                    <div v-for="(preview, idx) in cuciPaidSlipPreviews" :key="idx" class="relative group">
                                        <img 
                                            :src="preview.url" 
                                            :alt="preview.name" 
                                            class="w-full h-24 object-cover rounded-lg border-2 border-emerald-400"
                                        />
                                        <button 
                                            type="button"
                                            class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600 shadow"
                                            @click="removeCuciPaidSlip(idx)"
                                        >
                                            <i class="fas fa-times"></i>
                                        </button>
                                        <p class="text-xs text-gray-500 truncate mt-1">{{ preview.name }}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <!-- File input -->
                            <div v-if="(getFiles('cuci_paid_slips').length + cuciPaidSlips.length) < 3">
                                <label class="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-emerald-300 rounded-lg cursor-pointer bg-white hover:bg-emerald-50 transition-colors">
                                    <div class="flex flex-col items-center justify-center">
                                        <i class="fas fa-cloud-upload-alt text-2xl text-emerald-500 mb-1"></i>
                                        <p class="text-xs text-gray-500">คลิกเพื่อเลือกรูป หรือลากไฟล์มาวาง</p>
                                        <p class="text-xs text-gray-400">รองรับ JPG, PNG (สูงสุด 20MB)</p>
                                    </div>
                                    <input 
                                        type="file" 
                                        class="hidden" 
                                        accept="image/jpeg,image/jpg,image/png"
                                        multiple
                                        @change="onCuciPaidSlipChange"
                                    />
                                </label>
                            </div>
                            <p v-else class="text-xs text-amber-600 mt-2">
                                <i class="fas fa-info-circle mr-1"></i>
                                อัปโหลดครบ 3 รูปแล้ว
                            </p>
                            <p v-if="validationErrors.cuci_paid_slips" class="mt-1 text-xs text-red-600">{{ validationErrors.cuci_paid_slips }}</p>
                        </div>
                        
                        <p v-if="formError" class="text-sm text-red-600">{{ formError }}</p>
                        
                        <!-- ข้อความอธิบายเงื่อนไขส่งอีเมล -->
                        <div class="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p class="text-xs text-blue-700 font-medium mb-1"><i class="fas fa-info-circle mr-1"></i>เงื่อนไขการส่งอีเมลอัตโนมัติ</p>
                            <p class="text-xs text-blue-600">ระบบจะส่งอีเมลแจ้งผลไปยังผู้ยื่นคำขอโดยอัตโนมัติเมื่อมีการเปลี่ยนสถานะคำขอ (รวมถึงสถานะ "ขอหลักฐานเพิ่ม")</p>
                        </div>
                        
                        <div class="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between gap-3">
                            <div class="order-2 sm:order-1">
                                <!-- Send Email Button -->
                                <button 
                                    type="button" 
                                    v-if="hasEmail"
                                    class="w-full sm:w-auto px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-60"
                                    :disabled="sendingEmail"
                                    @click="handleSendEmail"
                                >
                                    <span v-if="sendingEmail"><i class="fas fa-spinner fa-spin mr-1"></i>กำลังส่ง...</span>
                                    <span v-else><i class="fas fa-envelope mr-1"></i>ส่งอีเมลแจ้งผล</span>
                                </button>
                                <span v-else class="text-xs text-gray-500 italic">ไม่มีอีเมลผู้รับ</span>
                            </div>
                            <div class="order-1 sm:order-2 flex w-full sm:w-auto gap-2 sm:gap-3">
                                <button type="button" class="flex-1 sm:flex-initial px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded hover:bg-gray-300" @click="resetForm" :disabled="saving">
                                    รีเซ็ต
                                </button>
                                <button
                                    type="submit"
                                    class="flex-1 sm:flex-initial px-4 sm:px-6 py-2 text-sm font-semibold text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-60"
                                    :disabled="saving"
                                >
                                    <span v-if="saving"><i class="fas fa-spinner fa-spin mr-1"></i>กำลังบันทึก...</span>
                                    <span v-else><i class="fas fa-save mr-1"></i>บันทึก</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </section>
            </div>
        </div>

        <!-- Lightbox with Zoom -->
        <div 
            v-if="lightbox.visible" 
            class="fixed inset-0 z-40 flex items-center justify-center bg-black/90" 
            @click.self="closeLightbox"
            @wheel.prevent="handleLightboxWheel"
        >
            <!-- Close button -->
            <button 
                type="button" 
                class="absolute top-4 right-4 z-50 text-white bg-black/60 hover:bg-black/80 rounded-full w-10 h-10 flex items-center justify-center transition-colors" 
                @click="closeLightbox"
            >
                <i class="fas fa-times text-lg"></i>
            </button>
            
            <!-- Previous button -->
            <button 
                v-if="lightbox.allImages.length > 1"
                type="button" 
                class="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white bg-black/60 hover:bg-black/80 disabled:opacity-30 disabled:cursor-not-allowed rounded-full w-12 h-12 flex items-center justify-center transition-colors" 
                :disabled="!canGoPrev"
                @click="goToPrevImage"
            >
                <i class="fas fa-chevron-left text-xl"></i>
            </button>
            
            <!-- Next button -->
            <button 
                v-if="lightbox.allImages.length > 1"
                type="button" 
                class="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white bg-black/60 hover:bg-black/80 disabled:opacity-30 disabled:cursor-not-allowed rounded-full w-12 h-12 flex items-center justify-center transition-colors" 
                :disabled="!canGoNext"
                @click="goToNextImage"
            >
                <i class="fas fa-chevron-right text-xl"></i>
            </button>
            
            <!-- Zoom controls -->
            <div class="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-2 bg-black/60 rounded-full px-3 py-2">
                <button 
                    type="button" 
                    class="w-8 h-8 text-white hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                    @click="zoomOut"
                    :disabled="lightbox.zoom <= 0.5"
                    :class="{ 'opacity-50 cursor-not-allowed': lightbox.zoom <= 0.5 }"
                >
                    <i class="fas fa-minus"></i>
                </button>
                <button 
                    type="button" 
                    class="px-3 py-1 text-white text-sm hover:bg-white/20 rounded transition-colors min-w-[60px]"
                    @click="resetZoom"
                >
                    {{ Math.round(lightbox.zoom * 100) }}%
                </button>
                <button 
                    type="button" 
                    class="w-8 h-8 text-white hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
                    @click="zoomIn"
                    :disabled="lightbox.zoom >= 5"
                    :class="{ 'opacity-50 cursor-not-allowed': lightbox.zoom >= 5 }"
                >
                    <i class="fas fa-plus"></i>
                </button>
            </div>
            
            <!-- Image container -->
            <div 
                class="relative max-w-full max-h-full overflow-hidden select-none"
                :class="{ 'cursor-grab': lightbox.zoom > 1, 'cursor-grabbing': lightbox.isDragging }"
                @mousedown="startDrag"
                @mousemove="onDrag"
                @mouseup="endDrag"
                @mouseleave="endDrag"
                @touchstart.prevent="handleTouchStart"
                @touchmove.prevent="handleTouchMove"
                @touchend="handleTouchEnd"
            >
                <img 
                    :src="lightbox.imageUrl" 
                    :alt="lightbox.caption" 
                    class="max-w-[95vw] max-h-[90vh] lg:max-w-[100vw] lg:max-h-[95vh] object-contain rounded transition-transform duration-100"
                    :style="lightboxImageStyle"
                    @dblclick="handleDoubleClick"
                    draggable="false"
                />
            </div>
            
            <!-- Caption -->
            <p class="absolute bottom-6 left-0 right-0 text-center text-sm text-white px-4">
                <span v-if="lightbox.allImages.length > 1" class="bg-black/50 px-2 py-1 rounded mr-2">{{ currentImagePosition }}</span>
                {{ lightbox.caption }}
            </p>
            
            <!-- Zoom hint -->
            <p class="absolute top-4 left-4 text-xs text-white/60">ดับเบิลคลิกหรือใช้ scroll เพื่อซูม • ลากเพื่อเลื่อนดูรูป • ใช้ลูกศรซ้าย/ขวาเปลี่ยนรูป</p>
        </div>

        <!-- Compression Progress Overlay -->
        <div v-if="isCompressing" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div class="bg-white rounded-xl p-6 text-center shadow-xl max-w-xs w-full mx-4">
                <div class="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto"></div>
                <h3 class="text-lg font-semibold text-gray-800 mt-4">กำลังบีบอัดรูปภาพ...</h3>
                <p class="text-sm text-gray-600 mt-1 truncate">{{ currentCompressingFile }}</p>
                <div class="w-full h-2 bg-gray-200 rounded-full mt-4 overflow-hidden">
                    <div class="h-full bg-emerald-500 rounded-full transition-all duration-300" :style="{ width: compressionProgress + '%' }"></div>
                </div>
                <p class="text-sm font-medium text-emerald-600 mt-2">{{ compressionProgress }}%</p>
                <p class="text-xs text-gray-500 mt-2">กรุณารอสักครู่ ระบบกำลังลดขนาดไฟล์</p>
            </div>
        </div>
    </div>
</template>

<script>


import FloodReliefAdminService from '@/services/FloodReliefAdminService'
import imageCompression from 'browser-image-compression'

const STATUS_PRESENTATION = {
    1: { label: 'รอตรวจสอบ', chipClass: 'bg-yellow-100 text-yellow-800', borderClass: 'border-yellow-200' },
    2: { label: 'อนุมัติแล้ว', chipClass: 'bg-green-100 text-green-800', borderClass: 'border-green-200' },
    3: { label: 'ไม่อนุมัติ', chipClass: 'bg-red-100 text-red-800', borderClass: 'border-red-200' },
    4: { label: 'โอนแล้ว', chipClass: 'bg-emerald-100 text-emerald-800', borderClass: 'border-emerald-200' },
    5: { label: 'ขอหลักฐานเพิ่ม', chipClass: 'bg-amber-100 text-amber-800', borderClass: 'border-amber-200' },
    default: { label: 'ไม่ทราบสถานะ', chipClass: 'bg-gray-100 text-gray-700', borderClass: 'border-gray-200' },
}

const STATUS_OPTIONS = Object.entries(STATUS_PRESENTATION)
    .filter(([key]) => key !== 'default')
    .map(([key, config]) => ({ value: Number(key), label: config.label }))

const ATTACHMENT_GROUPS = [
    { key: 'payment_slips', title: 'หลักฐานการโอน / สลิป' },
    { key: 'book_bank_photos', title: 'หน้าสมุดบัญชี' },
    { key: 'refueling_photos', title: 'รูประหว่างการเติมน้ำมัน' },
    { key: 'cuci_paid_slips', title: 'สลิปการโอนเงินจาก CUCI' },
]

const currencyFormatter = new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const dateFormatter = new Intl.DateTimeFormat('th-TH', { dateStyle: 'medium' })
const dateTimeFormatter = new Intl.DateTimeFormat('th-TH', { dateStyle: 'medium', timeStyle: 'short' })

function deriveAssetBaseUrl() {
    const apiUrl = process.env.VUE_APP_URL_API || ''
    if (!apiUrl) {
        return window.location.origin + '/'
    }
    return apiUrl.replace(/\/api\/?$/, '/')
}

export default {
    name: 'FloodReliefFuelRequestManage',
    data() {
        return {
            loading: true,
            saving: false,
            sendingEmail: false,
            error: null,
            formError: null,
            request: null,
            form: {
                status: '',
                admin_notes: '',
                approved_amount: null,
            },
            validationErrors: {},
            lightbox: {
                visible: false,
                imageUrl: '',
                caption: '',
                zoom: 1,
                translateX: 0,
                translateY: 0,
                isDragging: false,
                startX: 0,
                startY: 0,
                currentIndex: 0,
                allImages: [],
                currentGroupTitle: '',
            },
            statusOptions: STATUS_OPTIONS,
            attachmentGroups: ATTACHMENT_GROUPS,
            assetBaseUrl: deriveAssetBaseUrl(),
            // Image upload for cuci_paid_slips
            cuciPaidSlips: [],
            cuciPaidSlipPreviews: [],
            isCompressing: false,
            compressionProgress: 0,
            currentCompressingFile: '',
        }
    },
    computed: {
        fieldProvinces() {
            return this.parseProvinces(this.request ? this.request.field_provinces : [])
        },
        hasEmail() {
            if (!this.request) return false
            return !!(this.request.coordinator_email || (this.request.member && this.request.member.mem_email))
        },
        lightboxImageStyle() {
            return {
                transform: `scale(${this.lightbox.zoom}) translate(${this.lightbox.translateX}px, ${this.lightbox.translateY}px)`,
            }
        },
        canGoPrev() {
            return this.lightbox.currentIndex > 0
        },
        canGoNext() {
            return this.lightbox.currentIndex < this.lightbox.allImages.length - 1
        },
        currentImagePosition() {
            return `${this.lightbox.currentIndex + 1} / ${this.lightbox.allImages.length}`
        },
    },
    watch: {
        '$route.params.id': {
            handler() {
                this.fetchRequest()
            }
        }
    },
    mounted() {
        this.fetchRequest()
        window.addEventListener('keyup', this.handleEscKey)
    },
    beforeUnmount() {
        window.removeEventListener('keyup', this.handleEscKey)
    },
    methods: {
        async fetchRequest() {
            this.loading = true
            this.error = null
            this.validationErrors = {}
            this.formError = null
            this.$Progress.start()
            try {
                const response = await FloodReliefAdminService.getRequestDetail(this.$route.params.id)
                this.request = (response && response.data && response.data.data) || null
                this.hydrateForm()
            } catch (err) {
                this.error = this.extractError(err)
            } finally {
                this.loading = false
                this.$Progress.finish()
            }
        },
        hydrateForm() {
            if (!this.request) {
                return
            }
            // Ensure status is a number
            const rawStatus = this.request.status
            this.form.status = (rawStatus !== null && rawStatus !== undefined && rawStatus !== '') ? Number(rawStatus) : ''
            this.form.admin_notes = this.request.admin_notes || ''
            this.form.approved_amount = this.request.approved_amount || null
            this.validationErrors = {}
            // Clear uploaded files
            this.cuciPaidSlips = []
            this.cuciPaidSlipPreviews = []
        },
        getStatusConfig(status) {
            return STATUS_PRESENTATION[status] || STATUS_PRESENTATION.default
        },
        getStatusLabel(status) {
            return this.getStatusConfig(status).label
        },
        getStatusChipClass(status) {
            return this.getStatusConfig(status).chipClass
        },
        getStatusBorderClass(status) {
            return this.getStatusConfig(status).borderClass
        },
        formatCurrency(value) {
            return currencyFormatter.format(value || 0)
        },
        formatDate(dateStr) {
            if (!dateStr) {
                return '-'
            }
            return dateFormatter.format(new Date(dateStr))
        },
        formatDateTime(dateStr) {
            if (!dateStr) {
                return '-'
            }
            return dateTimeFormatter.format(new Date(dateStr))
        },
        parseProvinces(value) {
            if (!value) {
                return []
            }
            if (Array.isArray(value)) {
                return value.filter(Boolean)
            }
            if (typeof value === 'string') {
                try {
                    const parsed = JSON.parse(value)
                    return Array.isArray(parsed) ? parsed.filter(Boolean) : []
                } catch (error) {
                    return value ? [value] : []
                }
            }
            return []
        },
        getProvinceDisplay(request) {
            if (!request) {
                return '-'
            }
            if (request.organization_province === 'อื่นๆ') {
                return request.other_organization_province || '-'
            }
            return request.organization_province || '-'
        },
        resolveBankName(request) {
            if (!request) {
                return '-'
            }
            if (request.bank_name === 'อื่นๆ') {
                return request.other_bank_name || '-'
            }
            return request.bank_name || '-'
        },
        getApproverName(request) {
            if (!request) {
                return '-'
            }
            const approver = request.approved_by_user
            if (approver) {
                const parts = [approver.name, approver.lastname].filter(Boolean)
                return parts.length ? parts.join(' ') : (approver.email || '-')
            }
            return request.approved_by ? `User #${request.approved_by}` : '-'
        },
        getFiles(key) {
            if (!this.request || !this.request[key]) {
                return []
            }
            return Array.isArray(this.request[key]) ? this.request[key] : []
        },
        getFileUrl(file, variant = 'original') {
            if (!file) {
                return ''
            }
            if (variant === 'thumbnail' && file.thumbnail_url) {
                return file.thumbnail_url
            }
            if (variant === 'thumbnail' && file.thumbnail_path) {
                return this.normalizeAssetUrl(file.thumbnail_path)
            }
            if (file.original_url) {
                return file.original_url
            }
            if (file.path) {
                return this.normalizeAssetUrl(file.path)
            }
            const folder = variant === 'thumbnail' ? 'images/floodReliefMission/thumbnail' : 'images/floodReliefMission/original'
            return this.normalizeAssetUrl(`${folder}/${file.filename}`)
        },
        normalizeAssetUrl(path) {
            if (!path) {
                return ''
            }
            if (/^https?:\/\//i.test(path)) {
                return path
            }
            const base = this.assetBaseUrl.replace(/\/+$/, '')
            const cleanPath = String(path).replace(/^\/+/, '')
            return `${base}/${cleanPath}`
        },
        openLightbox(file, title, groupKey = null) {
            // Build list of all images from the same group
            let allImages = []
            let currentIndex = 0
            
            if (groupKey) {
                allImages = this.getFiles(groupKey)
                currentIndex = allImages.findIndex(f => (f.id && f.id === file.id) || (f.filename && f.filename === file.filename))
                if (currentIndex === -1) currentIndex = 0
            } else {
                // Get all images from all attachment groups
                this.attachmentGroups.forEach(group => {
                    const files = this.getFiles(group.key)
                    files.forEach(f => {
                        allImages.push({ ...f, groupTitle: group.title })
                    })
                })
                currentIndex = allImages.findIndex(f => (f.id && f.id === file.id) || (f.filename && f.filename === file.filename))
                if (currentIndex === -1) currentIndex = 0
            }
            
            this.lightbox.visible = true
            this.lightbox.allImages = allImages
            this.lightbox.currentIndex = currentIndex
            this.lightbox.currentGroupTitle = title
            this.updateLightboxImage()
        },
        updateLightboxImage() {
            const file = this.lightbox.allImages[this.lightbox.currentIndex]
            if (!file) return
            
            this.lightbox.imageUrl = this.getFileUrl(file, 'original')
            const title = file.groupTitle || this.lightbox.currentGroupTitle
            this.lightbox.caption = `${title} • ${file.filename || 'ไฟล์แนบ'}`
            // Reset zoom state when changing image
            this.lightbox.zoom = 1
            this.lightbox.translateX = 0
            this.lightbox.translateY = 0
            this.lightbox.isDragging = false
        },
        goToPrevImage() {
            if (this.canGoPrev) {
                this.lightbox.currentIndex--
                this.updateLightboxImage()
            }
        },
        goToNextImage() {
            if (this.canGoNext) {
                this.lightbox.currentIndex++
                this.updateLightboxImage()
            }
        },
        closeLightbox() {
            this.lightbox.visible = false
            this.lightbox.imageUrl = ''
            this.lightbox.caption = ''
            this.lightbox.zoom = 1
            this.lightbox.translateX = 0
            this.lightbox.translateY = 0
            this.lightbox.allImages = []
            this.lightbox.currentIndex = 0
            this.lightbox.currentGroupTitle = ''
        },
        // Zoom functions
        zoomIn() {
            if (this.lightbox.zoom < 5) {
                this.lightbox.zoom = Math.min(5, this.lightbox.zoom + 0.5)
            }
        },
        zoomOut() {
            if (this.lightbox.zoom > 0.5) {
                this.lightbox.zoom = Math.max(0.5, this.lightbox.zoom - 0.5)
                // Reset position if zoom is back to 1 or less
                if (this.lightbox.zoom <= 1) {
                    this.lightbox.translateX = 0
                    this.lightbox.translateY = 0
                }
            }
        },
        resetZoom() {
            this.lightbox.zoom = 1
            this.lightbox.translateX = 0
            this.lightbox.translateY = 0
        },
        handleDoubleClick() {
            if (this.lightbox.zoom === 1) {
                // Zoom in to 2x
                this.lightbox.zoom = 2
            } else {
                // Reset to 1x
                this.resetZoom()
            }
        },
        handleLightboxWheel(event) {
            const delta = event.deltaY > 0 ? -0.25 : 0.25
            const newZoom = Math.max(0.5, Math.min(5, this.lightbox.zoom + delta))
            
            if (newZoom !== this.lightbox.zoom) {
                this.lightbox.zoom = newZoom
                // Reset position if zoom is 1 or less
                if (newZoom <= 1) {
                    this.lightbox.translateX = 0
                    this.lightbox.translateY = 0
                }
            }
        },
        // Drag/Pan functions
        startDrag(event) {
            if (this.lightbox.zoom <= 1) return
            this.lightbox.isDragging = true
            this.lightbox.startX = event.clientX - this.lightbox.translateX
            this.lightbox.startY = event.clientY - this.lightbox.translateY
        },
        onDrag(event) {
            if (!this.lightbox.isDragging || this.lightbox.zoom <= 1) return
            this.lightbox.translateX = event.clientX - this.lightbox.startX
            this.lightbox.translateY = event.clientY - this.lightbox.startY
        },
        endDrag() {
            this.lightbox.isDragging = false
        },
        // Touch support for mobile
        handleTouchStart(event) {
            if (event.touches.length === 1 && this.lightbox.zoom > 1) {
                // Single finger drag
                this.lightbox.isDragging = true
                this.lightbox.startX = event.touches[0].clientX - this.lightbox.translateX
                this.lightbox.startY = event.touches[0].clientY - this.lightbox.translateY
            } else if (event.touches.length === 2) {
                // Two finger pinch-to-zoom
                this.lightbox.isDragging = false
                this.lastTouchDistance = this.getTouchDistance(event.touches)
            }
        },
        handleTouchMove(event) {
            if (event.touches.length === 1 && this.lightbox.isDragging && this.lightbox.zoom > 1) {
                // Single finger drag
                this.lightbox.translateX = event.touches[0].clientX - this.lightbox.startX
                this.lightbox.translateY = event.touches[0].clientY - this.lightbox.startY
            } else if (event.touches.length === 2) {
                // Pinch-to-zoom
                const currentDistance = this.getTouchDistance(event.touches)
                if (this.lastTouchDistance) {
                    const scale = currentDistance / this.lastTouchDistance
                    const newZoom = Math.max(0.5, Math.min(5, this.lightbox.zoom * scale))
                    this.lightbox.zoom = newZoom
                    
                    if (newZoom <= 1) {
                        this.lightbox.translateX = 0
                        this.lightbox.translateY = 0
                    }
                }
                this.lastTouchDistance = currentDistance
            }
        },
        handleTouchEnd() {
            this.lightbox.isDragging = false
            this.lastTouchDistance = null
        },
        getTouchDistance(touches) {
            const dx = touches[0].clientX - touches[1].clientX
            const dy = touches[0].clientY - touches[1].clientY
            return Math.sqrt(dx * dx + dy * dy)
        },
        handleEscKey(event) {
            if (event.key === 'Escape' && this.lightbox.visible) {
                this.closeLightbox()
            }
            // Arrow key navigation
            if (this.lightbox.visible) {
                if (event.key === 'ArrowLeft') {
                    this.goToPrevImage()
                } else if (event.key === 'ArrowRight') {
                    this.goToNextImage()
                }
            }
        },
        // Image compression helper
        async compressImage(file, maxSizeMB = 1.5) {
            // Skip compression if file is already small enough
            if (file.size <= maxSizeMB * 1024 * 1024) {
                return file
            }

            const options = {
                maxSizeMB,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                initialQuality: 0.8,
                fileType: 'image/jpeg',
                onProgress: (progress) => {
                    this.compressionProgress = progress
                }
            }

            try {
                const compressedBlob = await imageCompression(file, options)
                console.log(`Compressed ${file.name}: ${(file.size/1024/1024).toFixed(2)}MB → ${(compressedBlob.size/1024/1024).toFixed(2)}MB`)
                
                // Create new File object with correct .jpg extension
                const originalName = file.name.replace(/\\.[^/.]+$/, '') // Remove original extension
                const newFileName = `${originalName}.jpg`
                const compressedFile = new File([compressedBlob], newFileName, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                })
                
                return compressedFile
            } catch (error) {
                console.error('Compression failed:', error)
                return file // Return original if compression fails
            }
        },
        // Handle cuci paid slip file change
        async onCuciPaidSlipChange(e) {
            const files = Array.from(e.target.files)
            const existingCount = this.getFiles('cuci_paid_slips').length
            const totalFiles = existingCount + this.cuciPaidSlips.length + files.length
            
            // Validate file count (max 3)
            if (totalFiles > 3) {
                this.$swal.fire({
                    icon: 'warning',
                    title: 'ไฟล์เกินจำนวนที่กำหนด',
                    text: `อัปโหลดสลิปการโอนได้สูงสุด 3 รูป (มีอยู่แล้ว ${existingCount} รูป, รอบันทึก ${this.cuciPaidSlips.length} รูป)`,
                    confirmButtonColor: '#059669'
                })
                e.target.value = ''
                return
            }
            
            const validFiles = []
            const validPreviews = []
            
            this.isCompressing = true
            
            for (const file of files) {
                // Check file size (max 20MB before compression)
                if (file.size > 20 * 1024 * 1024) {
                    this.$swal.fire({
                        icon: 'warning',
                        title: 'ไฟล์ขนาดใหญ่เกินไป',
                        text: `ไฟล์ ${file.name} มีขนาดเกิน 20MB`,
                        confirmButtonColor: '#059669'
                    })
                    continue
                }
                
                // Check file type
                if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
                    this.$swal.fire({
                        icon: 'warning',
                        title: 'ประเภทไฟล์ไม่ถูกต้อง',
                        text: `ไฟล์ ${file.name} ไม่ใช่รูปภาพ (รองรับ jpg, jpeg, png)`,
                        confirmButtonColor: '#059669'
                    })
                    continue
                }
                
                // Compress image
                this.currentCompressingFile = file.name
                this.compressionProgress = 0
                const compressedFile = await this.compressImage(file, 1.5)
                
                validFiles.push(compressedFile)
                validPreviews.push({
                    url: URL.createObjectURL(compressedFile),
                    name: file.name
                })
            }
            
            this.isCompressing = false
            this.currentCompressingFile = ''
            this.compressionProgress = 0
            
            this.cuciPaidSlips = [...this.cuciPaidSlips, ...validFiles]
            this.cuciPaidSlipPreviews = [...this.cuciPaidSlipPreviews, ...validPreviews]
            
            // Clear server error if exists
            if (this.validationErrors.cuci_paid_slips) {
                delete this.validationErrors.cuci_paid_slips
            }
            
            // Reset input
            e.target.value = ''
        },
        // Remove cuci paid slip (new uploads - not yet saved)
        removeCuciPaidSlip(index) {
            URL.revokeObjectURL(this.cuciPaidSlipPreviews[index].url)
            this.cuciPaidSlips.splice(index, 1)
            this.cuciPaidSlipPreviews.splice(index, 1)
        },
        // Delete existing cuci paid slip from server
        async deleteExistingCuciPaidSlip(fileId) {
            const result = await this.$swal.fire({
                title: 'ยืนยันการลบ?',
                text: 'คุณต้องการลบสลิปนี้หรือไม่? การดำเนินการนี้ไม่สามารถเรียกคืนได้',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#dc2626',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'ลบ',
                cancelButtonText: 'ยกเลิก',
            })
            
            if (!result.isConfirmed) {
                return
            }
            
            this.$Progress.start()
            try {
                const response = await FloodReliefAdminService.deleteCuciPaidSlip(this.request.id, fileId)
                this.request = (response && response.data && response.data.data) || this.request
                this.$swal.fire({
                    icon: 'success',
                    title: 'ลบสำเร็จ',
                    text: 'ลบสลิปเรียบร้อยแล้ว',
                    confirmButtonColor: '#059669',
                })
            } catch (err) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'ไม่สามารถลบได้',
                    text: this.extractError(err),
                    confirmButtonColor: '#dc2626',
                })
            } finally {
                this.$Progress.finish()
            }
        },
        resetForm() {
            this.hydrateForm()
            this.validationErrors = {}
            this.formError = null
        },
        validateForm() {
            const errors = {}
            const statusNum = Number(this.form.status)
            if (this.form.status === '' || this.form.status === null || isNaN(statusNum) || ![1, 2, 3, 4, 5].includes(statusNum)) {
                errors.status = 'กรุณาเลือกสถานะ'
            }
            // ต้องระบุเหตุผลเมื่อไม่อนุมัติ (status 3) หรือขอหลักฐานเพิ่ม (status 5)
            if ([3, 5].includes(statusNum) && !this.form.admin_notes.trim()) {
                errors.admin_notes = statusNum === 5 
                    ? 'กรุณาระบุรายละเอียดหลักฐานที่ต้องการเพิ่มเติม'
                    : 'กรุณาอธิบายเหตุผลเมื่อไม่อนุมัติคำขอ'
            }
            this.validationErrors = errors
            return Object.keys(errors).length === 0
        },
        normalizeErrors(errors = {}) {
            const normalized = {}
            Object.keys(errors || {}).forEach((key) => {
                const value = errors[key]
                normalized[key] = Array.isArray(value) ? value[0] : value
            })
            return normalized
        },
        async handleSubmit() {
            this.formError = null
            if (!this.validateForm()) {
                return
            }
            
            // SweetAlert confirmation
            const result = await this.$swal.fire({
                title: 'ยืนยันการบันทึก?',
                text: 'คุณต้องการบันทึกการเปลี่ยนแปลงนี้หรือไม่?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#059669',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'ยืนยัน',
                cancelButtonText: 'ยกเลิก',
            })
            
            if (!result.isConfirmed) {
                return
            }
            
            this.saving = true
            this.$Progress.start()
            try {
                const payload = {
                    status: Number(this.form.status),
                    admin_notes: this.form.admin_notes || null,
                    approved_amount: this.form.approved_amount || null,
                    cuci_paid_slips: this.cuciPaidSlips,
                }
                const response = await FloodReliefAdminService.updateRequest(this.request.id, payload)
                this.request = (response && response.data && response.data.data) || this.request
                this.hydrateForm()
                this.$swal.fire({
                    icon: 'success',
                    title: 'บันทึกสำเร็จ',
                    text: 'ระบบได้อัปเดตสถานะคำขอแล้ว',
                    confirmButtonColor: '#059669',
                })
            } catch (err) {
                if (err.response && err.response.status === 422) {
                    this.validationErrors = this.normalizeErrors(err.response.data.errors)
                    this.formError = err.response.data.message || 'ไม่สามารถบันทึกการเปลี่ยนแปลงได้'
                } else {
                    this.formError = this.extractError(err)
                }
            } finally {
                this.saving = false
                this.$Progress.finish()
            }
        },
        async handleSendEmail() {
            // SweetAlert confirmation
            const result = await this.$swal.fire({
                title: 'ส่งอีเมลแจ้งผล?',
                text: 'ระบบจะส่งอีเมลแจ้งข้อมูลคำขอปัจจุบันไปยังผู้ยื่นคำขอ',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#2563eb',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'ส่งอีเมล',
                cancelButtonText: 'ยกเลิก',
            })
            
            if (!result.isConfirmed) {
                return
            }
            
            this.sendingEmail = true
            this.$Progress.start()
            try {
                const response = await FloodReliefAdminService.sendEmail(this.request.id)
                const sentTo = (response && response.data && response.data.sent_to) || []
                this.$swal.fire({
                    icon: 'success',
                    title: 'ส่งอีเมลสำเร็จ',
                    html: `ส่งอีเมลไปยัง:<br/><strong>${sentTo.join('<br/>')}</strong>`,
                    confirmButtonColor: '#059669',
                })
            } catch (err) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'ไม่สามารถส่งอีเมลได้',
                    text: this.extractError(err),
                    confirmButtonColor: '#dc2626',
                })
            } finally {
                this.sendingEmail = false
                this.$Progress.finish()
            }
        },
        extractError(err) {
            if (err && err.response && err.response.data) {
                return err.response.data.message || 'ไม่สามารถโหลดข้อมูลได้'
            }
            return 'ไม่สามารถโหลดข้อมูลได้'
        },
        // Member/User helper methods
        getMemberFullName(member) {
            if (!member) return '-'
            const name = member.mem_name || ''
            const lastname = member.mem_lastname || ''
            return (name + ' ' + lastname).trim() || '-'
        },
        getGenderLabel(member) {
            if (!member || !member.mem_gender) return '-'
            const genderMap = {
                'male': 'ชาย',
                'female': 'หญิง',
                'other': 'อื่นๆ',
                'ชาย': 'ชาย',
                'หญิง': 'หญิง',
            }
            return genderMap[member.mem_gender] || member.mem_gender
        },
        getProviderLabel(member) {
            if (!member || !member.user_id) return 'ไม่ทราบ'
            // Check user provider from request if available
            const user = (this.request && this.request.user) || null
            if (user) {
                if (user.line_provider || user.line_user_id) return 'บัญชี Line'
                if (user.provider === 'google') return 'บัญชี Google'
                if (user.provider) return `บัญชี ${user.provider}`
            }
            // Fallback - check member data
            if (member.mem_line_id) return 'บัญชี Line'
            return 'กรอกฟอร์มสมัคร'
        },
        getProviderIcon(member) {
            const label = this.getProviderLabel(member)
            if (label.includes('Line')) return 'fab fa-line'
            if (label.includes('Google')) return 'fab fa-google'
            return 'fas fa-user-edit'
        },
        getProviderClass(member) {
            const label = this.getProviderLabel(member)
            if (label.includes('Line')) return 'bg-green-100 text-green-800'
            if (label.includes('Google')) return 'bg-red-100 text-red-800'
            return 'bg-gray-100 text-gray-800'
        },
    },
}
</script>

<style scoped>
/* Custom scrollbar for sections */
section::-webkit-scrollbar {
    width: 6px;
}
section::-webkit-scrollbar-thumb {
    background-color: #d1d5db;
    border-radius: 3px;
}
</style>
